'use client';

import type { ReactNode } from 'react';

import {
  awardRules,
  conductRules,
  problemRules,
  syllabus,
  syllabusScope,
  teamRules,
  type RuleEntry
} from '../competition-rules';
import { PageHeader } from '@/components/layout/page-header';
import { Tabs, TabsList, TabsPanel, TabsTab } from '@/components/ui';

interface RulesSectionProps {
  children: ReactNode;
  description?: string;
  id: string;
  title: string;
}

function RulesSection({ children, description, id, title }: RulesSectionProps) {
  return (
    <section aria-labelledby={id} className="grid gap-4">
      <div className="grid gap-1">
        <h2 className="m-0 font-display text-base font-semibold tracking-tight text-text" id={id}>
          {title}
        </h2>
        {description ? <p className="m-0 text-sm text-text-muted">{description}</p> : null}
      </div>
      {children}
    </section>
  );
}

function RuleLedger({ entries }: { entries: readonly RuleEntry[] }) {
  return (
    <dl className="m-0 grid divide-y divide-border-subtle border-y border-border-subtle">
      {entries.map((entry) => (
        <div
          className="grid gap-1 py-3 text-sm sm:grid-cols-4 sm:items-baseline sm:gap-4"
          key={entry.term}
        >
          <dt className="font-medium text-text">{entry.term}</dt>
          <dd className="m-0 text-text-muted sm:col-span-3">
            {entry.detail}
            {entry.example ? (
              <span className="block">
                Enter {entry.example.value} as{' '}
                <code className="font-mono text-text">{entry.example.entry}</code>
              </span>
            ) : null}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function SyllabusTabs() {
  return (
    <Tabs>
      <TabsList aria-label="Syllabus areas">
        {syllabus.map((area) => (
          <TabsTab id={area.id} key={area.id}>
            {area.label}
          </TabsTab>
        ))}
      </TabsList>
      {syllabus.map((area) => (
        <TabsPanel id={area.id} key={area.id}>
          <ul className="m-0 list-none gap-4 p-0 sm:columns-2">
            {area.topics.map((topic) => (
              <li className="break-inside-avoid py-1 text-sm text-text-muted" key={topic}>
                {topic}
              </li>
            ))}
          </ul>
        </TabsPanel>
      ))}
    </Tabs>
  );
}

export function RulesView() {
  return (
    <div className="mx-auto grid w-full max-w-detail content-start gap-12 sm:py-8">
      <PageHeader density="compact" title="Rules" />

      <RulesSection id="teams-title" title="Teams">
        <RuleLedger entries={teamRules} />
      </RulesSection>

      <RulesSection id="problems-title" title="Problems">
        <RuleLedger entries={problemRules} />
      </RulesSection>

      <RulesSection id="conduct-title" title="Conduct">
        <RuleLedger entries={conductRules} />
      </RulesSection>

      <RulesSection id="awards-title" title="Awards">
        <RuleLedger entries={awardRules} />
      </RulesSection>

      <RulesSection description={syllabusScope} id="syllabus-title" title="Syllabus">
        <SyllabusTabs />
      </RulesSection>
    </div>
  );
}
